import React from 'react';
import TableWrapper from '../wrappers/TableWrapper';
import { NumberTo2DP } from '../../utils/NumberTo2DP';

export default function MarketTable({tableData}) {
    let {loading, error, data} = tableData;

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Get color based on churn rate
    const getChurnColor = (rate) => {
        if (rate < 100) return 'text-green-600 bg-green-50';
        if (rate < 500) return 'text-yellow-600 bg-yellow-50';
        return 'text-red-600 bg-red-50';
    };

    if (loading) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#069125]"></div>
                    <p className="text-sm text-gray-500 mt-4">Loading marketing data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <span className="text-4xl mb-3">⚠️</span>
                    <h3 className="text-lg font-semibold text-gray-700">Error Loading Data</h3>
                    <p className="text-sm text-gray-500 mt-1">Failed to fetch marketing statistics</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-6 py-2 bg-[#069125] text-white text-sm font-medium rounded-xl hover:bg-[#057a1f] transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='w-full'>
            <div className='w-full flex flex-col gap-4'>
                <div className='bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden'>
                    <TableWrapper data={data} itemsPerPage={10}>
                        {({ data }) => {
                            let totalRevenue = data.length ? data?.map(item => item?.total_revenue ? item?.total_revenue : 0)?.reduce((accumulator, item) => {
                                return accumulator += item;
                            }, 0) : 0;

                            let totalCustomers = data.length ? data?.map(item => item?.total_customers ? item?.total_customers : 0)?.reduce((accumulator, item) => {
                                return accumulator += item;
                            }, 0) : 0;

                            let monthlyRevenue = data.length ? data?.map(item => item?.monthly_revenue ? item?.monthly_revenue : 0)?.reduce((accumulator, item) => {
                                return accumulator += item;
                            }, 0) : 0;

                            let activeCustomers = data.length ? data?.map(item => item?.active_customers ? item?.active_customers : 0)?.reduce((accumulator, item) => {
                                return accumulator += item;
                            }, 0) : 0;

                            let churnRate = data.length ? data?.map(item => item?.churn_rate ? item?.churn_rate : 0)?.reduce((accumulator, item) => {
                                return accumulator += item;
                            }, 0) : 0;

                            return (
                                <>
                                    {/* Desktop Table View */}
                                    <div className="hidden lg:block overflow-x-auto">
                                        <table className='w-full'>
                                            <thead>
                                                <tr className='bg-gradient-to-r from-gray-50 to-gray-100/80 border-b border-gray-200'>
                                                    <th className='px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                                                        <span className="flex items-center gap-1">🏢 Marketing Partner</span>
                                                    </th>
                                                    <th className='px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                                                        <span className="flex items-center justify-end gap-1">💰 Total Revenue</span>
                                                    </th>
                                                    <th className='px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                                                        <span className="flex items-center justify-end gap-1">👥 Total Customers</span>
                                                    </th>
                                                    <th className='px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                                                        <span className="flex items-center justify-end gap-1">📊 Monthly Revenue</span>
                                                    </th>
                                                    <th className='px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                                                        <span className="flex items-center justify-end gap-1">✅ Active Customers</span>
                                                    </th>
                                                    <th className='px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                                                        <span className="flex items-center justify-end gap-1">📉 Churn Rate</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data && data?.length > 0 ? (
                                                    <>
                                                        {data.map((keyword, index) => (
                                                            <tr 
                                                                key={index} 
                                                                className={`border-b border-gray-50 hover:bg-green-50/30 transition-colors duration-150 ${
                                                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                                                                }`}
                                                            >
                                                                <td className='px-4 py-3'>
                                                                    <div className='flex items-center gap-3'>
                                                                        <div className='w-10 h-10 rounded-xl bg-gradient-to-br from-[#069125]/10 to-[#057a1f]/10 flex items-center justify-center text-lg font-bold text-[#069125]'>
                                                                            {keyword?.keyword?.charAt(0) || 'M'}
                                                                        </div>
                                                                        <span className='text-sm font-medium text-gray-800'>
                                                                            {keyword?.keyword}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className='px-4 py-3 text-right text-sm font-semibold text-gray-800'>
                                                                    {formatCurrency(keyword?.total_revenue || 0)}
                                                                </td>
                                                                <td className='px-4 py-3 text-right text-sm text-gray-800'>
                                                                    {keyword?.total_customers?.toLocaleString() || 0}
                                                                </td>
                                                                <td className='px-4 py-3 text-right text-sm text-gray-800'>
                                                                    {formatCurrency(keyword?.monthly_revenue || 0)}
                                                                </td>
                                                                <td className='px-4 py-3 text-right text-sm text-gray-800'>
                                                                    {keyword?.active_customers?.toLocaleString() || 0}
                                                                </td>
                                                                <td className='px-4 py-3 text-right'>
                                                                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getChurnRateColor(keyword?.churn_rate || 0)}`}>
                                                                        {keyword?.churn_rate || 0}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {/* Total Row */}
                                                        <tr className='bg-gradient-to-r from-[#069125]/5 to-[#057a1f]/5 border-t-2 border-[#069125]/20'>
                                                            <td className='px-4 py-3.5'>
                                                                <span className='text-sm font-bold text-gray-800 flex items-center gap-2'>
                                                                    📊 TOTAL
                                                                </span>
                                                            </td>
                                                            <td className='px-4 py-3.5 text-right text-sm font-bold text-green-700'>
                                                                {formatCurrency(totalRevenue)}
                                                            </td>
                                                            <td className='px-4 py-3.5 text-right text-sm font-bold text-gray-800'>
                                                                {totalCustomers.toLocaleString()}
                                                            </td>
                                                            <td className='px-4 py-3.5 text-right text-sm font-bold text-gray-800'>
                                                                {formatCurrency(monthlyRevenue)}
                                                            </td>
                                                            <td className='px-4 py-3.5 text-right text-sm font-bold text-gray-800'>
                                                                {activeCustomers.toLocaleString()}
                                                            </td>
                                                            <td className='px-4 py-3.5 text-right text-sm font-bold text-gray-800'>
                                                                {churnRate}
                                                            </td>
                                                        </tr>
                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan={6} className='px-4 py-12 text-center'>
                                                            <div className="flex flex-col items-center gap-2">
                                                                <span className="text-3xl">📭</span>
                                                                <span className="text-sm text-gray-500">No marketing partners found</span>
                                                                <span className="text-xs text-gray-400">Try adjusting your filters</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className="lg:hidden">
                                        {data && data?.length > 0 ? (
                                            <div className="divide-y divide-gray-100">
                                                {data.map((keyword, index) => (
                                                    <div key={index} className="p-4 hover:bg-green-50/30 transition-colors">
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className='w-12 h-12 rounded-xl bg-gradient-to-br from-[#069125]/10 to-[#057a1f]/10 flex items-center justify-center text-xl font-bold text-[#069125]'>
                                                                    {keyword?.keyword?.charAt(0) || 'M'}
                                                                </div>
                                                                <div>
                                                                    <h3 className="font-semibold text-gray-800">{keyword?.keyword}</h3>
                                                                    <span className="text-xs text-gray-400">Partner #{index + 1}</span>
                                                                </div>
                                                            </div>
                                                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getChurnRateColor(keyword?.churn_rate || 0)}`}>
                                                                Churn: {keyword?.churn_rate || 0}
                                                            </span>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3 mt-2">
                                                            <div className="bg-blue-50 rounded-xl p-3">
                                                                <p className="text-xs text-gray-500">Total Revenue</p>
                                                                <p className="text-base font-bold text-blue-600">{formatCurrency(keyword?.total_revenue || 0)}</p>
                                                            </div>
                                                            <div className="bg-purple-50 rounded-xl p-3">
                                                                <p className="text-xs text-gray-500">Total Customers</p>
                                                                <p className="text-base font-bold text-purple-600">{keyword?.total_customers?.toLocaleString() || 0}</p>
                                                            </div>
                                                            <div className="bg-green-50 rounded-xl p-3">
                                                                <p className="text-xs text-gray-500">Monthly Revenue</p>
                                                                <p className="text-base font-bold text-green-600">{formatCurrency(keyword?.monthly_revenue || 0)}</p>
                                                            </div>
                                                            <div className="bg-orange-50 rounded-xl p-3">
                                                                <p className="text-xs text-gray-500">Active Customers</p>
                                                                <p className="text-base font-bold text-orange-600">{keyword?.active_customers?.toLocaleString() || 0}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {/* Total Card */}
                                                <div className="p-4 bg-gradient-to-r from-[#069125]/10 to-[#057a1f]/10">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-sm font-bold text-gray-800">📊 TOTAL</span>
                                                        <span className="text-xs text-gray-400">Summary</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div className="bg-white/70 rounded-xl p-3">
                                                            <p className="text-xs text-gray-500">Total Revenue</p>
                                                            <p className="text-base font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
                                                        </div>
                                                        <div className="bg-white/70 rounded-xl p-3">
                                                            <p className="text-xs text-gray-500">Total Customers</p>
                                                            <p className="text-base font-bold text-gray-800">{totalCustomers.toLocaleString()}</p>
                                                        </div>
                                                        <div className="bg-white/70 rounded-xl p-3">
                                                            <p className="text-xs text-gray-500">Monthly Revenue</p>
                                                            <p className="text-base font-bold text-green-600">{formatCurrency(monthlyRevenue)}</p>
                                                        </div>
                                                        <div className="bg-white/70 rounded-xl p-3">
                                                            <p className="text-xs text-gray-500">Active Customers</p>
                                                            <p className="text-base font-bold text-gray-800">{activeCustomers.toLocaleString()}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="p-8 text-center">
                                                <span className="text-3xl block">📭</span>
                                                <span className="text-sm text-gray-500 block mt-2">No marketing partners found</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            );
                        }}
                    </TableWrapper>
                </div>
            </div>
        </div>
    );
}

// Helper function for churn rate colors
const getChurnRateColor = (rate) => {
    if (rate < 100) return 'text-green-700 bg-green-100';
    if (rate < 500) return 'text-yellow-700 bg-yellow-100';
    return 'text-red-700 bg-red-100';
};